window.ContactPage = function ContactPage({ openQuoteModal }) {
  const [submitted, setSubmitted] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [emailError, setEmailError] = React.useState('');
  const [phoneError, setPhoneError] = React.useState('');
  const [selectedFile, setSelectedFile] = React.useState(null);

  const [rfqData, setRfqData] = React.useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    industry: 'Railway Test Systems',
    projectType: 'Custom Test Rig',
    componentName: '',
    testParameters: '',
    automationLevel: 'Fully Automated (PLC / SCADA)',
    timeline: '1-3 Months',
    message: ''
  });

  const [siteContactInfo, setSiteContactInfo] = React.useState({
    address: 'Novaket, Plot No. 3, Sipcot Ph. I, Govinda Agraharam, Hosur, Tamil Nadu, India. Pincode: 635126',
    phones: ['+91 95 977 30787', '+91 96 866 24595'],
    emails: ['info@novatek.co.in', 'it.team@novatek.co.in']
  });

  React.useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const res = await window.apiClient.get('content.php');
        if (res.success && res.data && res.data.contact_info) {
          const ci = res.data.contact_info;
          setSiteContactInfo({
            address: ci.address || 'Novaket, Plot No. 3, Sipcot Ph. I, Govinda Agraharam, Hosur, Tamil Nadu, India. Pincode: 635126',
            phones: [ci.phone || '', ci.phone2 || ''].filter(Boolean),
            emails: [ci.email || '', ci.supportEmail || ''].filter(Boolean)
          });
        }
      } catch (err) {
        console.warn('ContactPage contact info fetch warning:', err);
      }
    };

    fetchContactInfo();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (window.SEOHelper) {
      window.SEOHelper.updatePageMeta(
        "Contact Novatek | Submit Engineering RFQ & Specifications",
        "Submit your test rig drawing, CAD specification, or automation inquiry directly to Novatek's application engineering team in Hosur / Bengaluru, India."
      );
    }
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setEmailError('');
    setPhoneError('');

    if (!window.validateEmail(rfqData.email)) {
      setEmailError('Please enter a valid work email');
      return;
    }

    if (!rfqData.phone) {
      setPhoneError('Phone number is required');
      return;
    }

    const clean = rfqData.phone.replace(/\D/g, '');
    if (clean.length < 10) {
      setPhoneError('Phone number must be at least 10 digits');
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('type', 'contact_rfq');
      formData.append('name', rfqData.name);
      formData.append('email', rfqData.email);
      formData.append('phone', rfqData.phone);
      formData.append('company', rfqData.company);
      formData.append('sector', rfqData.industry);
      formData.append('subject', rfqData.projectType);
      
      // Detailed engineering parameters
      formData.append('componentName', rfqData.componentName || '');
      formData.append('testParameters', rfqData.testParameters || '');
      formData.append('automationLevel', rfqData.automationLevel || '');
      formData.append('timeline', rfqData.timeline || '');
      formData.append('message', rfqData.message || '');

      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      await window.apiClient.uploadFile('inquiries.php', formData);
    } catch (err) {
      console.warn('Inquiry upload warning:', err);
    } finally {
      setSubmitting(false);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setRfqData({
          name: '',
          email: '',
          phone: '',
          company: '',
          industry: 'Railway Test Systems',
          projectType: 'Custom Test Rig',
          componentName: '',
          testParameters: '',
          automationLevel: 'Fully Automated (PLC / SCADA)',
          timeline: '1-3 Months',
          message: ''
        });
        setSelectedFile(null);
      }, 3500);
    }
  };

  return (
    <div className="py-12 bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <span className="font-mono text-xs sm:text-sm font-bold text-secondary tracking-widest uppercase block">
            Application Engineering Bureau
          </span>
          <h1 className="font-sans text-3xl sm:text-5xl font-bold text-slate-900 tracking-tight">
            Submit Engineering RFQ & Specifications
          </h1>
          <div className="h-1 w-20 bg-secondary mx-auto rounded-full"></div>
          <p className="font-sans text-slate-600 text-base sm:text-lg">
            Have a difficult testing or automation challenge? Upload your CAD drawings, component specifications, or test standards directly to our design team.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          
          {/* Left Column: Direct Contact & Facility Info (5 Cols) */}
          <div className="lg:col-span-5 space-y-6">
            
            <div className="bg-slate-900 text-white rounded-2xl p-8 space-y-6 shadow-xl">
              <span className="font-mono text-xs font-bold text-secondary uppercase tracking-widest block">
                Engineering Headquarters
              </span>
              <h3 className="font-sans text-2xl font-bold text-white">
                NOVATEK Industrial Systems
              </h3>
              
              <div className="space-y-4 text-xs sm:text-sm font-sans text-slate-300">
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-secondary text-lg mt-0.5">location_on</span>
                  <div>
                    <strong className="text-white block font-sans">Facility & Design Bureau:</strong>
                    <span>{siteContactInfo.address}</span>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-secondary text-lg mt-0.5">mail</span>
                  <div className="space-y-1 w-full min-w-0">
                    <strong className="text-white block font-sans">Contact Email</strong>
                    <a
                      href={`mailto:${siteContactInfo.emails[0] || 'info@novatek.co.in'}`}
                      className="text-orange-400 hover:underline block break-all text-xs sm:text-sm font-sans"
                    >
                      {siteContactInfo.emails[0] || 'info@novatek.co.in'}
                    </a>
                    {siteContactInfo.emails[1] && (
                      <a
                        href={`mailto:${siteContactInfo.emails[1]}`}
                        className="text-orange-400 hover:underline block break-all text-xs sm:text-sm font-sans"
                      >
                        {siteContactInfo.emails[1]}
                      </a>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-secondary text-lg mt-0.5">call</span>
                  <div className="space-y-1 w-full min-w-0">
                    <strong className="text-white block font-sans">Direct Engineering Hotline:</strong>
                    <a
                      href={`tel:${siteContactInfo.phones[0] ? siteContactInfo.phones[0].replace(/\s+/g, '') : '+919597730787'}`}
                      className="text-orange-400 hover:underline block text-xs sm:text-sm font-sans"
                    >
                      {siteContactInfo.phones[0] || '+91 95977 30787'}
                    </a>
                    {siteContactInfo.phones[1] && (
                      <a
                        href={`tel:${siteContactInfo.phones[1].replace(/\s+/g, '')}`}
                        className="text-orange-400 hover:underline block text-xs sm:text-sm font-sans"
                      >
                        {siteContactInfo.phones[1]}
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Secondary WhatsApp Action */}
              <div className="pt-4 border-t border-slate-800">
                <a
                  href="https://wa.me/919597730787?text=Hello%20Novatek%20Engineering,%20I%20would%20like%20to%20discuss%20a%20test%20rig%20requirement."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-mono font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors w-full"
                >
                  <span className="material-symbols-outlined text-base">chat</span>
                  <span>Connect on WhatsApp (Secondary Hotline)</span>
                </a>
              </div>

            </div>

            {/* Embedded Google Maps View */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm flex flex-col">
              <div className="h-64 relative">
                <iframe 
                  title="Novaket Engineering Facility Map"
                  src="https://maps.google.com/maps?q=12.778100,77.805888&z=17&output=embed" 
                  className="w-full h-full border-0"
                  loading="lazy"
                ></iframe>
              </div>
              <div className="p-3 bg-slate-50 border-t border-slate-200 text-center">
                <a 
                  href="https://maps.app.goo.gl/pSn8NNRuwhFmpRKm9" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-secondary hover:text-orange-600 font-mono font-bold"
                >
                  <span className="material-symbols-outlined text-sm">open_in_new</span>
                  <span>View Novaket on Google Maps</span>
                </a>
              </div>
            </div>

          </div>

          {/* Right Column: Comprehensive RFQ Form (7 Cols) */}
          <div className="lg:col-span-7 bg-white rounded-2xl border border-slate-200 p-6 sm:p-10 shadow-sm">
            
            {submitted ? (
              <div className="text-center py-16 space-y-4 animate-fadeIn">
                <div className="h-16 w-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                  <span className="material-symbols-outlined text-4xl">check_circle</span>
                </div>
                <h3 className="font-sans text-2xl font-bold text-slate-900">RFQ Received by Design Bureau</h3>
                <p className="font-sans text-sm text-slate-600 max-w-md mx-auto">
                  Thank you for submitting your engineering specification. Our application engineering team in Hosur / Bengaluru will review your technical parameters and contact you within 24 business hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} className="space-y-6">
                
                <div className="border-b border-slate-100 pb-3">
                  <h3 className="font-sans text-xl font-bold text-slate-900">
                    Engineering Project Details
                  </h3>
                  <span className="text-xs font-sans text-slate-500">
                    Fill in your component and test parameters below.
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Ramesh Chandra"
                      value={rfqData.name}
                      onChange={(e) => setRfqData({ ...rfqData, name: e.target.value })}
                      className="w-full p-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-secondary outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-700 mb-1">
                      Organization / Company *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Indian Railways / TVS / Alstom"
                      value={rfqData.company}
                      onChange={(e) => setRfqData({ ...rfqData, company: e.target.value })}
                      className="w-full p-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-secondary outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-700 mb-1">
                      Work Email *
                    </label>
                    <input
                      type="email"
                      required
                      placeholder="name@organization.com"
                      value={rfqData.email}
                      onChange={(e) => setRfqData({ ...rfqData, email: e.target.value })}
                      className="w-full p-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-secondary outline-none"
                    />
                    {emailError && <span className="text-xs text-red-500 mt-1 block">{emailError}</span>}
                  </div>

                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-700 mb-1">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      required
                      placeholder="+91 98765 43210"
                      value={rfqData.phone}
                      onChange={(e) => setRfqData({ ...rfqData, phone: e.target.value })}
                      className="w-full p-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-secondary outline-none"
                    />
                    {phoneError && <span className="text-xs text-red-500 mt-1 block">{phoneError}</span>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-700 mb-1">
                      Target Industry
                    </label>
                    <select
                      value={rfqData.industry}
                      onChange={(e) => setRfqData({ ...rfqData, industry: e.target.value })}
                      className="w-full p-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-secondary outline-none bg-white font-sans"
                    >
                      <option value="Railway Test Systems">Railway Test Systems</option>
                      <option value="Automotive Test & Automation">Automotive Test & Automation</option>
                      <option value="Aerospace & Defence">Aerospace & Defence</option>
                      <option value="Industrial Automation & SPM">Industrial Automation & SPM</option>
                      <option value="R&D & Academic Research">R&D & Academic Research</option>
                      <option value="Other">Other Application</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-700 mb-1">
                      Machine / System Type
                    </label>
                    <select
                      value={rfqData.projectType}
                      onChange={(e) => setRfqData({ ...rfqData, projectType: e.target.value })}
                      className="w-full p-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-secondary outline-none bg-white font-sans"
                    >
                      <option value="Custom Test Rig">Custom Test Rig</option>
                      <option value="Automated Test Bench">Automated Test Bench</option>
                      <option value="Robotic Automation Cell">Robotic Automation Cell</option>
                      <option value="Special Purpose Machine (SPM)">Special Purpose Machine (SPM)</option>
                      <option value="End-of-Line (EOL) Validation Station">End-of-Line (EOL) Validation Station</option>
                      <option value="DAQ & Software System">DAQ & Software System</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-700 mb-1">
                      Product / Component Name
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Brake Valve, Damper, Actuator"
                      value={rfqData.componentName}
                      onChange={(e) => setRfqData({ ...rfqData, componentName: e.target.value })}
                      className="w-full p-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-secondary outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-700 mb-1">
                      Testing Parameters (Pressure, Force, Speed)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. 10 Bar, 50 kN, 3.5 m/s"
                      value={rfqData.testParameters}
                      onChange={(e) => setRfqData({ ...rfqData, testParameters: e.target.value })}
                      className="w-full p-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-secondary outline-none"
                    />
                  </div>
                </div>

                {/* Upload File Box */}
                <div>
                  <label className="block text-xs font-mono font-bold text-slate-700 mb-1">
                    Upload Specification / Drawing / RFQ Document (PDF, STEP, CAD, DWG)
                  </label>
                  <div className="border-2 border-dashed border-slate-300 hover:border-secondary rounded-xl p-4 text-center cursor-pointer transition-colors bg-slate-50 relative">
                    <input
                      type="file"
                      accept=".pdf,.step,.stp,.dwg,.dxf,.doc,.docx,.png,.jpg,.jpeg"
                      onChange={handleFileChange}
                      className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                    />
                    <div className="flex flex-col items-center gap-1 text-slate-500">
                      <span className="material-symbols-outlined text-2xl text-secondary">upload_file</span>
                      <span className="text-xs font-mono font-bold text-slate-700">
                        {selectedFile ? `Selected: ${selectedFile.name}` : 'Click or Drag & Drop Drawing / Specification PDF'}
                      </span>
                      <span className="text-[10px] text-slate-400">Confidential Engineering Review</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono font-bold text-slate-700 mb-1">
                    Scope of Requirement & Standard Tolerances
                  </label>
                  <textarea
                    rows="3"
                    placeholder="Describe testing cycles, applicable standards (RDSO, UIC, ISO), or delivery timeframe..."
                    value={rfqData.message}
                    onChange={(e) => setRfqData({ ...rfqData, message: e.target.value })}
                    className="w-full p-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-secondary outline-none"
                  ></textarea>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-secondary hover:bg-orange-600 text-white font-sans font-bold text-sm py-3.5 rounded-xl shadow-lg hover:shadow-orange-500/20 transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50"
                  >
                    <span className="material-symbols-outlined text-lg">engineering</span>
                    <span>{submitting ? 'Submitting Technical RFQ...' : 'Submit Engineering Consultation Request'}</span>
                  </button>
                </div>

              </form>
            )}

          </div>

        </div>

      </div>
    </div>
  );
};
